/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GarmResultsType,
  GarmFilter,
  GarmRiskLevelType,
  CategoryType,
} from '@insights_module/types/BrandSafety';
import artworkFallback from '@assets/images/artwork-fallback.svg';
import { ShowsQueryObject } from '@insights_module/types/Show';
import { CATEGORIES_NAME_MAPPER } from '@insights_module/constants/contextualAndEntity';
import { ContextualTaxonomy } from '@insights_module/types/ContextualAndEntity';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLContextualFilterQuery,
  AQLContextualQuery,
  AQLNameQuery,
  AQLNamedEntitiesFilterQuery,
  AQLNamedEntitiesQuery,
  AQLTypeQuery,
} from '@insights_module/adc_ql/queries';

export type ParamsType = { [key: string]: unknown };

export function getShowsQueryObject(
  params: ParamsType,
  includeUnprocessed = false
): ShowsQueryObject {
  const query = {
    all: [new AQLTypeQuery('eq', 'show')],
  } as any;

  const paramsArray = Object.entries(params);
  const firstKey = paramsArray[0][0];
  const firstValue = paramsArray[0][1];

  // temporally we check if all filters are removed, and if they are we add one that give us all processed shows.. but only if we don't want to include unprocessed
  if (
    paramsArray.length < 3 &&
    (firstKey === 'all' || firstKey === 'any') &&
    Array.isArray(firstValue) &&
    !firstValue.length &&
    !includeUnprocessed
  ) {
    query.all.push(
      new AQLBrandSafetyGarmQuery([
        new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
          gte: 'unrelated',
          lte: 'floor',
        }),
      ])
    );
  }

  paramsArray.forEach(([key, value]) => {
    if (value === null) {
      return;
    }

    if (key === 'all' || key === 'any') {
      query.all.push({ [key]: value });
      return;
    }

    if (key === 'brand_safety_garm') {
      query.all.push({ [key]: value });
      return;
    }

    if (key === 'brand_safety_iab') {
      query.all.push({ [key]: value });
      return;
    }

    if (key === 'contextual' && Array.isArray(value) && value.length) {
      query.all.push(...value);
      return;
    }

    if (key === 'named_entities' && Array.isArray(value) && value.length) {
      query.all.push(...value);
      return;
    }

    if (key === 'global_search' && typeof value === 'string' && value.length) {
      query.all.push(new AQLNameQuery('includes', value as string));
      return;
    }
  });

  return { query };
}

export const getGarmDataArray = (results: GarmResultsType): GarmFilter[] => {
  if (!results) {
    return [];
  }
  return Object.entries(results).map(
    ([key, value]) =>
      ({
        category: key,
        risk: value.toLowerCase(),
      }) as GarmFilter
  );
};

export function getGarmQueryObject(params: any) {
  return new AQLBrandSafetyGarmQuery(
    Object.entries(params).map(
      ([key, value]) =>
        new AQLBrandSafetyGarmFilterQuery(key as CategoryType, 'range', {
          lte: value as GarmRiskLevelType,
        })
    )
  );
}

export function getContextualQueryObjects(params: any, taxonomy: ContextualTaxonomy) {
  const wrapper: AQLContextualQuery[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === 'includes' || value === 'excludes') {
      wrapper.push(
        new AQLContextualQuery(value, [
          new AQLContextualFilterQuery('taxonomy', 'eq', taxonomy),
          new AQLContextualFilterQuery('name', 'eq', CATEGORIES_NAME_MAPPER[taxonomy][key]),
        ])
      );
    }
  });

  return wrapper;
}

export function getNamedEntitiesQueryObject(params: { [key: string]: string[] }) {
  const { include_named_entities } = params;
  const { exclude_named_entities } = params;

  return {
    named_entities: [
      ...include_named_entities.map(
        (entity) =>
          new AQLNamedEntitiesQuery('includes', [
            new AQLNamedEntitiesFilterQuery('value', 'wildcard', entity),
          ])
      ),
      ...exclude_named_entities.map(
        (entity) =>
          new AQLNamedEntitiesQuery('excludes', [
            new AQLNamedEntitiesFilterQuery('value', 'wildcard', entity),
          ])
      ),
    ],
  };
}

export const validateArtworkUrl = (str: string | undefined): string => {
  const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:png|gif|webp|jpeg|jpg|gif)/;

  if (str && regex.test(str)) {
    return str;
  }

  return artworkFallback;
};

/**
 * Return the number of full stars, the number of empty stars and if we should show a half star based on the rating
 * number of stars is presented as an array for easy iteration
 * @param rating
 * @returns
 */
export const calculateStartCount = (rating: number) => {
  // we need to round down the rating to get the number of full stars
  let wholeStarsNumber = Math.floor(rating);

  const modulo = rating % 1;

  // if the modulo is between 0.25 and 0.75 we should show a half star (e.g. 3.4 or 3.6)
  const shouldShowHalfStar = modulo > 0.25 && modulo <= 0.75;

  // if the modulo is greater than 0.75 we should add a full star (e.g. 3.8 or 3.9)
  if (modulo > 0.75) {
    wholeStarsNumber += 1;
  }

  // the number of empty stars is the difference between together the number of whole stars and half stars and 5
  const emptyStarsNumber = (shouldShowHalfStar ? 4 : 5) - wholeStarsNumber;

  return {
    fullStarArray: new Array(wholeStarsNumber).fill(0),
    shouldShowHalfStar,
    emptyStarsArray: new Array(emptyStarsNumber).fill(0),
  };
};

export const formatRatingCount = (rating?: number) => {
  if (!rating) {
    return 'N/A';
  }

  if (rating < 1000) {
    return rating;
  }

  if (rating < 1000000) {
    return Number(rating / 1000).toFixed(1) + 'K';
  }

  return Number(rating / 1000000).toFixed(1) + 'M';
};
